import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { blogPosts } from '../data/blogs';
import DishesSection from '../components/DishesSection';
import './BlogDetails.css';

const BlogDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const post = blogPosts.find(b => b.id === parseInt(id)) || blogPosts[0];

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <div className="blog-details-page">
      {/* READING PROGRESS BAR */}
      <motion.div className="reading-progress-bar" style={{ scaleX }} />

      {/* CINEMATIC HERO */}
      <section className="blog-details-hero">
        <div className="hero-parallax-container">
          <motion.img 
            src={post.img} 
            alt={post.title} 
            className="hero-parallax-img"
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
          <div className="hero-overlay-gradient" />
        </div>
        
        <div className="container hero-content-wrap">
          <motion.div
            className="post-header-info"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <motion.span 
              className="post-category-badge"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              {post.category}
            </motion.span>
            <h1 className="post-display-title">{post.title}</h1>
            
            <div className="post-author-meta">
              <div className="author-img-placeholder" />
              <div className="meta-text">
                <span className="author-name">By {post.author}</span>
                <span className="post-date">{post.date} • 6 min read</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <span>Scroll to Read</span>
          <div className="line" />
        </motion.div>
      </section>

      {/* ARTICLE CONTENT */}
      <section className="article-main-section">
        <div className="container article-grid">
          {/* SIDEBAR FOR QUOTES/META */}
          <aside className="article-sidebar">
            <motion.div 
              className="sidebar-card"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h4>Key Insights</h4>
              <p>“{post.excerpt}”</p>
              <div className="share-links">
                <span>Share:</span>
                <div className="share-icons">
                  <div className="icon-circle" />
                  <div className="icon-circle" />
                  <div className="icon-circle" />
                </div>
              </div>
            </motion.div>
          </aside>

          {/* MAIN TEXT */}
          <div className="article-rich-content">
            <motion.p 
              className="drop-cap"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              {post.content.trim().split('\n\n')[0]}
            </motion.p>

            {post.content.trim().split('\n\n').slice(1).map((para, i) => (
              <motion.p 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
              >
                {para}
              </motion.p>
            ))}

            <motion.div 
              className="article-accent-box"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <h3>Why it matters?</h3>
              <p>Understanding the provenance and processing of spices isn't just for chefs—it's for anyone who values health, heritage, and the pure joy of flavor.</p>
            </motion.div>

            <div className="article-footer">
              <div className="tags">
                <span className="tag">#Spices</span>
                <span className="tag">#Heritage</span>
                <span className="tag">#IndianCooking</span>
              </div>
              <Link to="/blogs" className="back-to-journal">
                <span className="arrow">←</span> Return to Journal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED DISHES SECTION */}
      <DishesSection
        title="Experience the Story"
        subtitle="Bring these insights to life with our curated recipes"
      />
    </div>
  );
};

export default BlogDetails;

